"use client";
import { useEffect, useRef, useState } from "react";

import { useOrders } from "./useOrders";
import { useAuth } from "@/store/auth";
import {
  markReviewPrompted,
  selectOrderToPromptForReview,
  type ReviewPromptCandidate,
} from "@/lib/reviewPrompt";
import type { OrderOutput } from "@/api/online-ordering-service/model";

/**
 * App-level (as opposed to OrderDetailScreen's single-order) review prompt
 * trigger. Fires on app open AND on app resume (tab/app becoming visible
 * again) — a physical counter pickup means the customer's app was closed or
 * on an unrelated screen when the order flipped to Delivered/Completed, so
 * OrderDetailScreen's useEffect never runs. This hook re-checks the
 * customer's recent orders at those two moments and surfaces the most recent
 * eligible-and-unprompted one for a modal (not a redirect — the customer may
 * have opened the app to reorder, not to review).
 *
 * Reuses useOrders() as-is (already no-ops when logged out, already a single
 * lightweight listMine() call) rather than duplicating fetch logic.
 */
/** "Maybe later" quiets app-level prompting for the rest of the session. */
const SNOOZE_KEY = "tappya_review_prompt_snoozed_v1";

function isSnoozed(): boolean {
  try {
    return sessionStorage.getItem(SNOOZE_KEY) === "1";
  } catch {
    return false;
  }
}

function snooze(): void {
  try {
    sessionStorage.setItem(SNOOZE_KEY, "1");
  } catch {
    /* storage unavailable */
  }
}

function toCandidate(o: OrderOutput): ReviewPromptCandidate {
  return {
    id: o.id,
    status: o.status,
    completedAt:
      o.completedTimestamp || o.deliveredTimestamp || o.updatedAt || o.createdAt || null,
  };
}

export function useReviewPromptTrigger() {
  const { isLoggedIn } = useAuth();
  const { orders, refresh } = useOrders();
  const [promptOrder, setPromptOrder] = useState<OrderOutput | null>(null);
  // Guards against re-opening the modal on re-renders while it's already
  // showing (e.g. an unrelated orders refetch while the modal is open), and
  // against re-selecting the same order again later in the session.
  const shownForOrderIdRef = useRef<string | null>(null);
  const snoozedRef = useRef(false);

  // refresh()'s identity changes whenever isLoggedIn flips, which would
  // otherwise force the mount/visibility effects below to re-subscribe. Keep
  // the latest refresh in a ref so those effects can have empty dep arrays
  // and truly only fire on mount / on visibility transitions.
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  // Fire once on mount (app open).
  useEffect(() => {
    void refreshRef.current();
  }, []);

  // Fire again whenever the app/tab becomes visible again (app resume) —
  // this is the case a physical counter pickup relies on: customer had the
  // app backgrounded/closed while the order was marked Completed elsewhere.
  useEffect(() => {
    function onVisibilityChange() {
      if (document.visibilityState === "visible") void refreshRef.current();
    }
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);

  // Whenever the order list changes (after either trigger above refetches
  // it), re-run the pure selector and open the modal if something qualifies.
  useEffect(() => {
    if (!isLoggedIn || orders.length === 0) return;
    if (promptOrder) return; // modal already open — don't swap targets under the customer
    // A dismissal is about prompting, not about one order: without this the
    // next unreviewed order popped up the moment the modal closed, once per
    // past order, blocking whatever screen (checkout) sat underneath.
    if (snoozedRef.current || isSnoozed()) return;

    const candidates = orders.map(toCandidate);
    const picked = selectOrderToPromptForReview(candidates);
    if (!picked || !picked.id) return;
    if (shownForOrderIdRef.current === picked.id) return;

    const fullOrder = orders.find((o) => o.id === picked.id) || null;
    if (!fullOrder) return;

    shownForOrderIdRef.current = picked.id;
    setPromptOrder(fullOrder);
  }, [orders, isLoggedIn, promptOrder]);

  function dismiss() {
    if (promptOrder?.id) markReviewPrompted(promptOrder.id);
    snoozedRef.current = true;
    snooze();
    setPromptOrder(null);
  }

  return { promptOrder, dismiss };
}
